import { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { getAllPayments, subscribeToPayments } from "@/utils/firebase";
import { Loader2, DownloadCloud, AlertCircle, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { collection, query, where, getDocs, doc, getDoc, orderBy, updateDoc, addDoc, setDoc } from "firebase/firestore";
import { useFirebase } from "@/contexts/FirebaseContext";
import { useAuth } from "@/contexts/AuthContext";
import { paymentService } from "@/services/paymentService";

interface Payment {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending';
  createdAt: string;
  updatedAt: string;
  enrollmentId?: string;
  instructorId?: string;
  instructorName?: string;
  lessonType?: string;
  description?: string;
  courseName?: string;
  paymentMethod?: 'card' | 'cash';
  paymentDate?: string;
  adminId?: string;
  adminName?: string;
  instructorPayment?: {
    amount: number;
    status: 'pending' | 'paid';
    installments: Array<{
      amount: number;
      date: string;
      status: 'pending' | 'paid';
    }>;
  };
}

type PaymentStatus = 'paid' | 'pending';

const PaymentsManagement = () => {
  const { getFirestore } = useFirebase();
  const { user } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  
  useEffect(() => {
    const fetchPayments = async () => {
      setLoading(true);
      try {
        const db = getFirestore();
        if (!db) throw new Error("Firestore is not initialized");

        // Fetch all payments
        const paymentsRef = collection(db, "payments");
        const paymentsQuery = query(paymentsRef, orderBy("createdAt", "desc"));
        const paymentsSnapshot = await getDocs(paymentsQuery);
        
        const paymentsData = paymentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Payment[];

        console.log("Fetched payments:", paymentsData);

        // For each payment with an enrollmentId, fetch the enrollment to get instructor info
        const paymentsWithInstructors = await Promise.all(
          paymentsData.map(async (payment) => {
            if (payment.enrollmentId) {
              try {
                const enrollmentRef = doc(db, "enrollments", payment.enrollmentId);
                const enrollmentSnap = await getDoc(enrollmentRef);
                
                if (enrollmentSnap.exists()) {
                  const enrollment = enrollmentSnap.data();
                  return {
                    ...payment,
                    instructorId: enrollment.instructorId,
                    instructorName: enrollment.instructorName,
                    lessonType: enrollment.lessonType
                  };
                }
              } catch (error) {
                console.error(`Error fetching enrollment ${payment.enrollmentId}:`, error);
              }
            }
            return payment;
          })
        );

        // Fetch instructor payments for each payment
        const paymentsWithInstructorPayments = await Promise.all(
          paymentsWithInstructors.map(async (payment) => {
            if (payment.instructorId) {
              try {
                const instructorPaymentsRef = collection(db, "instructor_payments");
                const q = query(
                  instructorPaymentsRef,
                  where("paymentId", "==", payment.id)
                );
                const instructorPaymentsSnap = await getDocs(q);
                const instructorPayments = instructorPaymentsSnap.docs.map(doc => doc.data());
                
                if (instructorPayments.length > 0) {
                  const totalPaid = instructorPayments.reduce((sum, p) => sum + p.amount, 0);
                  const totalInstructorAmount = payment.lessonType === 'practical' 
                    ? Math.round(payment.amount * 0.3)
                    : 0;
                  
                  return {
                    ...payment,
                    instructorPayment: {
                      amount: totalInstructorAmount,
                      status: (totalPaid >= totalInstructorAmount ? 'paid' : 'pending') as PaymentStatus,
                      installments: instructorPayments.map(p => ({
                        amount: p.amount,
                        date: p.createdAt,
                        status: 'paid' as PaymentStatus
                      }))
                    }
                  };
                }
              } catch (error) {
                console.error(`Error fetching instructor payments for ${payment.id}:`, error);
              }
            }
            return payment;
          })
        );

        console.log("Payments with instructor info and payments:", paymentsWithInstructorPayments);
        setPayments(paymentsWithInstructorPayments);
      } catch (error) {
        console.error("Error fetching payments:", error);
        toast({
          title: "Error",
          description: "Failed to load payment data",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [getFirestore, toast]);

  const handlePayInstructor = async (payment: Payment) => {
    try {
      const instructorAmount = Math.round(payment.amount * 0.3);
      const paymentRef = doc(getFirestore(), 'payments', payment.id);
      
      const instructorPayment: NonNullable<Payment['instructorPayment']> = {
        amount: instructorAmount,
        status: 'paid' as PaymentStatus,
        installments: [{
          amount: instructorAmount,
          date: new Date().toISOString(),
          status: 'paid' as PaymentStatus
        }]
      };

      await updateDoc(paymentRef, {
        instructorPayment
      });

      // Create instructor payment record
      const instructorPaymentRef = doc(collection(getFirestore(), 'instructor_payments'));
      await setDoc(instructorPaymentRef, {
        paymentId: payment.id,
        instructorId: payment.instructorId,
        instructorName: payment.instructorName,
        amount: instructorAmount,
        status: 'paid' as PaymentStatus,
        createdAt: new Date().toISOString(),
        studentPayment: payment.amount,
        lessonType: payment.lessonType
      });

      setPayments((prevPayments: Payment[]) => 
        prevPayments.map((p: Payment): Payment => 
          p.id === payment.id 
            ? { 
                ...p, 
                instructorPayment: {
                  amount: instructorAmount,
                  status: 'paid' as PaymentStatus,
                  installments: [{
                    amount: instructorAmount,
                    date: new Date().toISOString(),
                    status: 'paid' as PaymentStatus
                  }]
                }
              }
            : p
        )
      );

      toast({
        title: "Success",
        description: `Paid instructor ${formatCurrency(instructorAmount)}`,
        variant: "default",
        duration: 3000
      } as const);
    } catch (error) {
      console.error('Error paying instructor:', error);
      toast({
        title: "Error",
        description: "Failed to process instructor payment",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleMarkCashPaymentAsPaid = async (payment: Payment) => {
    try {
      if (!user) {
        throw new Error("User not authenticated");
      }

      const adminName = `${user.firstName} ${user.lastName}`.trim() || "Admin";

      await paymentService.markCashPaymentAsPaid(
        payment.id,
        user.id,
        adminName
      );

      // Update local state
      setPayments(prevPayments =>
        prevPayments.map(p =>
          p.id === payment.id
            ? {
                ...p,
                status: 'paid',
                paymentDate: new Date().toISOString(),
                adminId: user.id,
                adminName
              }
            : p
        )
      );

      toast({
        title: "Success",
        description: "Payment marked as paid successfully",
      });
    } catch (error) {
      console.error("Error marking payment as paid:", error);
      toast({
        title: "Error",
        description: "Failed to mark payment as paid",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">Paid</Badge>;
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-600 border-yellow-200">Pending</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };
  
  const downloadPaymentsCSV = () => {
    const headers = ['ID', 'User', 'Email', 'Amount', 'Status', 'Date', 'Description', 'Course', 'Instructor'];
    
    const csvRows = [
      headers.join(','),
      ...payments.map(payment => [
        payment.id,
        `"${payment.userName}"`,
        payment.userEmail,
        formatCurrency(payment.amount),
        payment.status,
        format(new Date(payment.createdAt), 'yyyy-MM-dd HH:mm:ss'),
        `"${payment.description}"`,
        payment.courseName ? `"${payment.courseName}"` : '',
        payment.instructorName ? `"${payment.instructorName}"` : ''
      ].join(','))
    ];
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `payments-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "CSV Downloaded",
      description: "Payment records have been downloaded as CSV",
    });
  };
  
  return (
    <DashboardLayout role="admin">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Finance</h1>
      </div>
      
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Instructor Payments</CardTitle>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={downloadPaymentsCSV}
              disabled={payments.length === 0}
            >
              <DownloadCloud className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-10 bg-gray-50 rounded-lg">
              <AlertCircle className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <p className="text-lg font-medium text-gray-900">No Payments Found</p>
              <p className="text-gray-500 mt-1">There are no payment records in the system yet</p>
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Instructor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => {
                    const totalInstructorAmount = payment.lessonType === 'practical' 
                      ? Math.round(payment.amount * 0.3)
                      : 0;
                    const paidAmount = payment.instructorPayment?.installments?.reduce((sum, i) => sum + i.amount, 0) || 0;
                    const remainingAmount = totalInstructorAmount - paidAmount;

                    return (
                      <TableRow key={payment.id}>
                        <TableCell className="font-mono text-xs">{payment.id.slice(0, 8)}...</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{payment.userName}</div>
                            <div className="text-gray-500 text-xs">{payment.userEmail}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div>{payment.description}</div>
                            {payment.courseName && (
                              <div className="text-gray-500 text-xs">{payment.courseName}</div>
                            )}
                            {payment.lessonType && (
                              <div className="text-gray-500 text-xs capitalize">{payment.lessonType} lesson</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(payment.amount)}
                          {payment.lessonType === 'practical' && (
                            <div className="text-xs text-gray-500 mt-1">
                              (Instructor share: {formatCurrency(totalInstructorAmount)})
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {payment.instructorName ? (
                            <div>
                              <div className="font-medium">{payment.instructorName}</div>
                              {payment.instructorPayment ? (
                                <div className="text-xs text-gray-500">
                                  Paid: {formatCurrency(paidAmount)}
                                  {remainingAmount > 0 && (
                                    <div className="text-xs text-gray-400 mt-1">
                                      Remaining: {formatCurrency(remainingAmount)}
                                    </div>
                                  )}
                                </div>
                              ) : payment.lessonType === 'practical' && (
                                <div className="text-xs text-gray-500">
                                  Pending: {formatCurrency(totalInstructorAmount)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-500 text-sm">Not assigned</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {getStatusBadge(payment.status)}
                            {payment.instructorPayment && (
                              <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                                {payment.instructorPayment.status === 'paid' ? 'Instructor Paid' : 'Instructor Pending'}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-500 whitespace-nowrap">
                          {format(new Date(payment.createdAt), 'MMM d, yyyy h:mm a')}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {payment.status === 'paid' && 
                             payment.lessonType === 'practical' && 
                             remainingAmount > 0 && (
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePayInstructor(payment);
                                }}
                                disabled={!payment.instructorId || payment.instructorPayment?.status === 'paid'}
                              >
                                {payment.instructorPayment?.status === 'paid' ? 'Paid' : 'Pay Instructor'}
                              </Button>
                            )}
                            {payment.paymentMethod === 'cash' && payment.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMarkCashPaymentAsPaid(payment)}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Mark as Paid
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default PaymentsManagement;
